import React, { useState, useEffect, lazy, Suspense } from 'react';
import { toast } from 'sonner';
import {
  HeroSection, ServicesSection, AboutSection, ReviewsCarousel,
  ContactSection, LeaveReviewSection, Footer, Header,
  ProductsSection, GalletasSection, CakesSection,
  PanaderiaSection, DesayunosSection, MenuSaladoSection, BebidasSection
} from '../components/sections';
import FaqSection from '../components/sections/FaqSection';
// Modales/configuradores: lazy + render condicional → no viajan en el bundle
// inicial de la home (aligera el JS de arranque, mejora TBT/INP y la velocidad
// con que React monta, que es lo que condiciona el LCP en este SPA).
const OrderDialog = lazy(() => import('../components/modals/OrderDialog'));
const CakeConfigurator = lazy(() => import('../components/modals/CakeConfigurator'));
const TresLechesConfigurator = lazy(() => import('../components/modals/TresLechesConfigurator'));
const PanJamonDialog = lazy(() => import('../components/modals/PanJamonDialog'));
const ProductModal = lazy(() => import('../components/modals/ProductModal'));
const ImageLightbox = lazy(() => import('../components/modals/ImageLightbox'));
import { services, businessInfo, locations, cakeConfigOptions } from '../mock';
import { getImageUrl } from '../utils/imageUtils';
import {
  businessHours, getNextOpenDateTime, getBusinessHoursForDate,
  getOpenStatus
} from '../utils/businessHours';
import { loadCart as loadCartFromStorage, saveCart as saveCartToStorage, clearCart as clearCartStorage } from '../utils/cartStorage';
import useProducts from '../hooks/useProducts';
import Seo from '../components/Seo';
import { getOrganizationJsonLd, getLocalBusinessJsonLd, getWebSiteJsonLd, getFaqJsonLd } from '../lib/structuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const BRAZOS_INITIAL_COUNT = 8;

const Home = () => {
  const headerRef = React.useRef(null);
  // Los modales (lazy + Suspense) se montan SOLO tras hidratar en el cliente real,
  // nunca en el prerender. Asi el arbol hidratado de "/" no tiene ningun boundary
  // de Suspense (cuyos marcadores SSR el prerender-snapshot no conserva -> #418).
  const [mounted, setMounted] = useState(false);

  // Filters/search
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [brazosSearchTerm, setBrazosSearchTerm] = useState('');
  const [showAllBrazos, setShowAllBrazos] = useState(false);

  // Order/cart dialogs and selections
  const [orderDialog, setOrderDialog] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productType, setProductType] = useState('brazo');
  const [selectedSize, setSelectedSize] = useState('full');
  const [cart, setCart] = useState(() => loadCartFromStorage() || []);

  // Estado de apertura POR LOCAL (la Matriz cierra domingos; la Sucursal abre
  // 09:00-13:00). El aviso "cerrados" solo se muestra si AMBOS estan cerrados.
  // Primer render SIN banner (anyOpen:true) para que el HTML prerenderizado y la
  // primera pintura del cliente COINCIDAN -> hidratacion limpia (hydrateRoot).
  // El estado real por local se calcula tras montar (efecto), nunca en prerender.
  const [openStatus, setOpenStatus] = useState({ matriz: true, sucursal: true, anyOpen: true });
  const showAfterHoursWarning = !openStatus.anyOpen;
  const [selectedFlavors, setSelectedFlavors] = useState({});
  const [customQuantity, setCustomQuantity] = useState(13);

  // Lightbox
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxTitle, setLightboxTitle] = useState('');

  // Cake configurator
  const [cakeConfigDialog, setCakeConfigDialog] = useState(false);
  const [selectedCake, setSelectedCake] = useState(null);
  const [cakeConfig, setCakeConfig] = useState({ size: 'mediana', sabor: '', relleno: '', exterior: '', topping: '' });

  // Tres Leches configurator
  const [tresLechesDialog, setTresLechesDialog] = useState(false);
  const [tresLechesConfig, setTresLechesConfig] = useState({
    isCombo: false, medianaFlavor: '', grandeFlavor: '', singleSize: 'mediana', singleFlavor: ''
  });

  // Product detail modal
  const [productDetailDialog, setProductDetailDialog] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailProductType, setDetailProductType] = useState('');
  const [detailSelectedSize, setDetailSelectedSize] = useState('full');
  const [detailSelectedOption, setDetailSelectedOption] = useState(null);
  const [detailQuantity, setDetailQuantity] = useState(1);

  // Pan Jamón
  const [panJamonDialog, setPanJamonDialog] = useState(false);
  const [panJamonOption, setPanJamonOption] = useState('tradicional');

  // Reviews
  const [googleReviews, setGoogleReviews] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [dynamicLocations, setDynamicLocations] = useState(null);
  const reviewsPerPage = 4;
  const autoScrollInterval = 5000;

  // Delivery
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [deliveryError, setDeliveryError] = useState(null);
  const [customerCoords, setCustomerCoords] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');

  // Schedule
  const [scheduledDate, setScheduledDate] = useState(() => getNextOpenDateTime().date);
  const [scheduledTime, setScheduledTime] = useState(() => getNextOpenDateTime().time);

  // Order form
  const [customerName, setCustomerName] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('tienda');
  const [selectedLocation, setSelectedLocation] = useState('matriz');
  const [comments, setComments] = useState('');

  // WhatsApp config
  const [whatsappPhone, setWhatsappPhone] = useState(businessInfo.phone);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Nº de pedido: se genera UNA vez al abrir el carrito para que la vista
  // previa y el pedido enviado coincidan.
  const [orderNumber, setOrderNumber] = useState('');

  // Products from backend
  const {
    brazosGitanos, galletas, galletasEspeciales, packs, pasteles,
    panaderia, desayunos, menuSalado, bebidasFrias, bebidasCalientes,
    loadingProducts
  } = useProducts();

  const categories = ['Todos', 'Clásico', 'Especial', 'Premium'];

  const filteredBrazos = brazosGitanos
    .filter(b => (b.availableSlice !== false || b.availableFull !== false))
    .filter(brazo => {
      const categoryMatch = selectedCategory === 'Todos' || brazo.category?.toLowerCase() === selectedCategory.toLowerCase();
      const searchMatch = brazosSearchTerm === '' || brazo.name.toLowerCase().includes(brazosSearchTerm.toLowerCase());
      return categoryMatch && searchMatch;
    });

  // Validate scheduled time
  const validateScheduledTime = () => {
    const location = deliveryMethod === 'tienda' ? selectedLocation : 'matriz';
    const hours = getBusinessHoursForDate(scheduledDate, location);
    if (!hours) return { valid: false, error: 'Cerrado ese día. Por favor selecciona otra fecha.', blocking: true };

    const [selectedHour, selectedMin] = scheduledTime.split(':').map(Number);
    const [openHour, openMin] = hours.open.split(':').map(Number);
    const [closeHour, closeMin] = hours.close.split(':').map(Number);
    const selectedMinutes = selectedHour * 60 + selectedMin;
    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;

    if (selectedMinutes < openMinutes || selectedMinutes > closeMinutes) {
      return { valid: false, error: `Horario de atención: ${hours.open} - ${hours.close}`, blocking: true };
    }

    const today = new Date().toISOString().split('T')[0];
    if (scheduledDate === today) {
      const now = new Date();
      const selectedDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
      const diffMinutes = (selectedDateTime - now) / (1000 * 60);
      if (diffMinutes < 120 && diffMinutes > 0) {
        return { valid: true, warning: '⚡ Pedido para pronto - Confirmaremos disponibilidad', blocking: false };
      }
    }
    return { valid: true, error: null, blocking: false };
  };

  // Mide la altura del header y la expone como CSS var --brazo-header-h en <html>
  // (NO como estado de React). El hero usa esa var en su padding-top con un valor
  // CONSTANTE, así el HTML prerenderizado y el primer render del cliente son
  // idénticos -> hidratación limpia (antes se horneaba "padding-top:108px" en el
  // SSR pero el cliente arrancaba en 0 -> React #418). <html> no lo hidrata React.
  React.useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () =>
      document.documentElement.style.setProperty(
        '--brazo-header-h', `${el.getBoundingClientRect().height}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showAfterHoursWarning]);

  // On mount: load reviews, check store hours
  useEffect(() => {
    const loadGoogleReviews = async () => {
      setLoadingReviews(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/google-reviews`);
        const data = await response.json();
        setGoogleReviews(data);
      } catch (error) {
        console.error('Error loading Google reviews:', error);
      } finally {
        setLoadingReviews(false);
      }
    };

    const loadLocationsReviews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/locations-reviews`);
        const data = await response.json();
        if (data.locations) setDynamicLocations(data.locations);
      } catch (error) {
        console.error('Error loading locations reviews:', error);
      }
    };

    const loadWhatsAppConfig = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/config`);
        if (response.ok) {
          const data = await response.json();
          if (data.whatsapp_number) setWhatsappPhone(data.whatsapp_number);
        }
      } catch (error) {
        // use default
      }
    };

    // En el prerender (Chrome headless) NO disparamos cargas de datos que NO van
    // sembrados en el HTML (reviews, locations, config WhatsApp) ni el estado de
    // horario: el snapshot queda con el render INICIAL (carrusel de reseñas vacio,
    // sin banner) y asi coincide con el primer render del cliente -> hidratacion
    // limpia (antes el prerender horneaba las reseñas pero el cliente arrancaba en
    // null -> React #418). Los productos SI se cargan: van sembrados (useProducts).
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    setMounted(true);

    loadWhatsAppConfig();
    loadGoogleReviews();
    loadLocationsReviews();

    // Notify if cart was restored from localStorage (within 24h TTL)
    if (cart.length > 0) {
      toast.info('Tienes productos guardados en tu carrito', {
        description: 'Recuperamos tu pedido anterior. Puedes seguir comprando o finalizarlo.',
        duration: 5000,
      });
    }

    const checkStoreHours = () => setOpenStatus(getOpenStatus());
    checkStoreHours();
    const interval = setInterval(checkStoreHours, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist cart to localStorage whenever it changes (24h TTL inside cartStorage util)
  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  // Deep link to "La Reina" via ?producto=la-reina
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('producto') === 'la-reina') {
      const timer = setTimeout(() => {
        const laReina = pasteles.find(p => p.slug === 'la-reina' || p.name.toLowerCase().includes('la reina'));
        if (laReina) {
          openCakeConfigurator(laReina);
          window.history.replaceState({}, '', window.location.pathname);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pasteles]);

  const handleOrderClick = (product, type = 'brazo') => {
    setSelectedProduct(product);
    const finalType = product.packType || type;
    setProductType(finalType);
    if (finalType === 'brazo') setSelectedSize('full');
    else if (['pack', 'fixed', 'custom'].includes(finalType)) {
      setSelectedFlavors({});
      if (finalType === 'custom') setCustomQuantity(13);
    }
    setOrderDialog(true);
  };

  const handleFlavorChange = (flavorId, quantity) => {
    setSelectedFlavors(prev => ({ ...prev, [flavorId]: quantity }));
  };

  const getTotalSelectedFlavors = () => Object.values(selectedFlavors).reduce((sum, qty) => sum + (qty || 0), 0);

  const calculateCustomPrice = () => {
    const qty = customQuantity;
    if (qty <= 12) return 14.00;
    if (qty >= 13 && qty <= 48) return qty * 1.17;
    return qty * 1.10;
  };

  // GPS-based delivery
  const handleGetLocation = async () => {
    setLoadingDelivery(true);
    setDeliveryError(null);
    setDeliveryInfo(null);
    setCustomerCoords(null);
    if (!navigator.geolocation) {
      setDeliveryError('Tu navegador no soporta geolocalización');
      setLoadingDelivery(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCustomerCoords({ lat: latitude, lng: longitude });
        try {
          const response = await fetch(`${BACKEND_URL}/api/delivery/calculate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lat: latitude, lng: longitude })
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.detail || 'Error al calcular el envío');
          setDeliveryInfo(data);
        } catch (error) {
          setDeliveryError(error.message || 'Error al calcular el costo de envío');
        } finally {
          setLoadingDelivery(false);
        }
      },
      (error) => {
        let errorMessage = 'Error al obtener tu ubicación';
        if (error.code === error.PERMISSION_DENIED) errorMessage = 'Permiso de ubicación denegado. Por favor, activa el GPS en tu navegador.';
        else if (error.code === error.POSITION_UNAVAILABLE) errorMessage = 'Ubicación no disponible';
        else if (error.code === error.TIMEOUT) errorMessage = 'Tiempo de espera agotado';
        setDeliveryError(errorMessage);
        setLoadingDelivery(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const addToCart = () => {
    let item;
    if (productType === 'brazo') {
      const sizeText = selectedSize === 'full' ? 'Brazo completo' : 'Porción individual';
      const price = selectedSize === 'full' ? selectedProduct.priceFull : selectedProduct.priceSlice;
      const isMenuReina = selectedProduct.category === 'Menú de la Reina';
      item = { type: 'Brazo Gitano', name: selectedProduct.name, size: sizeText, price, quantity: 1, isMenuReina };
    } else if (productType === 'galleta') {
      item = { type: 'Galleta', name: selectedProduct.name, size: 'Unidad', price: selectedProduct.priceUnit || 1.50, quantity: 1 };
    } else if (['pack', 'fixed', 'custom'].includes(productType)) {
      const totalSelected = getTotalSelectedFlavors();
      const requiredQty = productType === 'custom' ? customQuantity : selectedProduct.quantity;
      if (totalSelected !== requiredQty) {
        toast.error(`Por favor selecciona exactamente ${requiredQty} galletas`);
        return;
      }
      const flavorsText = Object.entries(selectedFlavors)
        .filter(([_, qty]) => qty > 0)
        .map(([id, qty]) => {
          let flavor = galletas.find(g => String(g.id) === String(id));
          if (!flavor) flavor = galletasEspeciales.find(g => String(g.id) === String(id));
          return `${flavor?.name || 'Desconocido'} (${qty})`;
        }).join(', ');
      const price = productType === 'custom' ? calculateCustomPrice() : selectedProduct.price;
      item = { type: 'Pack de Galletas', name: selectedProduct.name, size: `${requiredQty} galletas: ${flavorsText}`, price, quantity: 1 };
    }
    if (item) {
      setCart([...cart, item]);
      setOrderDialog(false);
      setCartOpen(true);
      setSelectedFlavors({});
    }
  };

  const openProductDetail = (product, type) => {
    setDetailProduct(product);
    setDetailProductType(type);
    setDetailSelectedSize(type === 'brazo' ? 'full' : 'unit');
    if (product.hasOption && product.options && product.options.length > 0) {
      setDetailSelectedOption(product.options[0].name);
    } else {
      setDetailSelectedOption(null);
    }
    setDetailQuantity(1);
    setProductDetailDialog(true);
  };

  const isQuantityName = (p) => {
    const n = (p?.name || '').toLowerCase();
    return n.includes('cachito') || n.includes('empanada');
  };

  const addToCartFromDetail = () => {
    let item;
    if (detailProductType === 'brazo') {
      const sizeText = detailSelectedSize === 'full' ? 'Brazo completo' : 'Porción individual';
      const price = detailSelectedSize === 'full' ? detailProduct.priceFull : detailProduct.priceSlice;
      const isMenuReina = detailProduct.category === 'Menú de la Reina';
      item = { type: 'Brazo Gitano', name: detailProduct.name, size: sizeText, price, quantity: 1, isMenuReina };
    } else if (detailProductType === 'galleta') {
      item = { type: 'Galleta', name: detailProduct.name, size: 'Unidad', price: detailProduct.priceUnit || 1.50, quantity: detailQuantity };
    } else if (['panaderia', 'menu_sal', 'desayuno', 'bebida'].includes(detailProductType)) {
      const hasVariants = detailProduct.hasOption && detailProduct.options && detailProduct.options.length > 0;
      let selectedVariant = null;
      let variantPrice = detailProduct.price || 0;
      if (hasVariants && detailSelectedOption) {
        selectedVariant = detailProduct.options.find(opt => opt.name === detailSelectedOption);
        variantPrice = selectedVariant?.price || detailProduct.price || 0;
      }
      item = {
        type: detailProductType === 'panaderia' ? 'Panadería' :
              detailProductType === 'menu_sal' ? 'Menú de Sal' :
              detailProductType === 'desayuno' ? 'Desayuno' : 'Bebida',
        name: detailProduct.name,
        size: hasVariants && selectedVariant ? selectedVariant.name : 'Unidad',
        price: variantPrice,
        quantity: (['desayuno', 'menu_sal'].includes(detailProductType) || !hasVariants || isQuantityName(detailProduct)) ? detailQuantity : 1,
        hasVariants,
        selectedVariant: selectedVariant ? selectedVariant.name : null
      };
    } else if (detailProductType === 'galleta_especial') {
      item = { type: 'Galleta Especial', name: detailProduct.name, size: 'Unidad', price: detailProduct.price || 0, quantity: detailQuantity };
    }
    if (item) {
      setCart([...cart, item]);
      setProductDetailDialog(false);
      setCartOpen(true);
    }
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

const getTotalCart = () => cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  const generateOrderNumber = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}.${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  };

  // Generar el número de pedido al abrir el carrito (estable mientras está abierto)
  useEffect(() => {
    if (cartOpen) setOrderNumber(generateOrderNumber());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartOpen]);

  const handleWhatsAppOrder = async () => {
    if (cart.length === 0 && !selectedProduct) {
      const message = encodeURIComponent('Hola, vengo del sitio web. Deseo realizar un pedido.');
      window.open(`https://wa.me/${whatsappPhone}?text=${message}`, '_blank');
      return;
    }
    if (!customerName.trim()) { toast.error('Por favor ingresa tu nombre'); return; }
    if (!customerPhone.trim()) { toast.error('Por favor ingresa tu teléfono de contacto'); return; }

    const timeValidation = validateScheduledTime();
    if (timeValidation.blocking && !timeValidation.valid) {
      toast.error(timeValidation.error || 'Horario no válido. Por favor selecciona una hora dentro del horario de atención.');
      return;
    }
    if (selectedProduct && cart.length === 0) { addToCart(); return; }

    setIsSubmitting(true);
    try {
    const finalOrderNumber = orderNumber || generateOrderNumber();
    let message = 'PEDIDO WEB - BRAZOLOVERS\n========================\n\n';
    message += `Pedido: ${finalOrderNumber}\n`;
    message += `Fecha: ${new Date().toLocaleDateString('es-EC')}\n`;
    message += `Hora: ${new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}\n\n`;
    message += `Cliente: ${customerName}\nTel: ${customerPhone}\n\n`;

    const isToday = scheduledDate === new Date().toISOString().split('T')[0];
    const formattedDate = new Date(scheduledDate + 'T00:00:00').toLocaleDateString('es-EC', { weekday: 'short', day: 'numeric', month: 'short' });
    message += `ENTREGA:\n${isToday ? 'HOY' : formattedDate} - ${scheduledTime}\n\nPRODUCTOS:\n`;

    let total = 0;
    cart.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n   ${item.size} - $${item.price.toFixed(2)} x${item.quantity}\n`;
      total += item.price * item.quantity;
    });

    message += `\n------------------------\nSubtotal: $${total.toFixed(2)}\n`;
    if (deliveryMethod === 'tienda') {
      const locationName = selectedLocation === 'matriz' ? 'Matriz' : 'Sucursal';
      message += `Retiro: ${locationName}\nTOTAL: $${total.toFixed(2)}\n`;
    } else {
      message += 'Delivery\n';
      if (deliveryInfo && customerCoords) {
        message += `Dir: ${deliveryInfo.address || 'Ver mapa'}\n`;
        message += `Mapa: https://maps.google.com/?q=${customerCoords.lat},${customerCoords.lng}\n`;
        message += `Dist: ${deliveryInfo.distance_km}km - ${deliveryInfo.estimated_time}\n`;
        if (deliveryInfo.beyond_max_distance) {
          message += `Envio: CONSULTAR (mas de 10km)\nTotal productos: $${total.toFixed(2)}\n`;
        } else {
          message += `Envio: $${deliveryInfo.delivery_cost.toFixed(2)}\nTOTAL: $${(total + deliveryInfo.delivery_cost).toFixed(2)}\n`;
        }
      } else {
        message += `Total productos: $${total.toFixed(2)}\n+ envio segun distancia\n`;
      }
    }
    if (comments.trim()) message += `\nNota: ${comments}\n`;
    message += '\n========================\nGracias por su pedido!';

    try {
      await fetch(`${BACKEND_URL}/api/orders/backup-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: finalOrderNumber,
          customer_name: customerName,
          order_text: message,
          cart_items: cart,
          delivery_method: deliveryMethod,
          delivery_info: deliveryInfo,
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime,
          customer_phone: customerPhone,
          comments,
          selected_location: deliveryMethod === 'delivery' ? 'matriz' : selectedLocation
        })
      });
    } catch (error) {
      console.error('Error enviando email de respaldo:', error);
    }

    toast.success('¡Pedido registrado!', {
      description: `Pedido #${finalOrderNumber} - Redirigiendo a WhatsApp...`,
      duration: 4000
    });

    window.open(`https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`, '_blank');

    // Clear persisted cart after successful checkout
    clearCartStorage();

    setCart([]);
    setCustomerName('');
    setCustomerPhone('');
    setComments('');
    setDeliveryMethod('tienda');
    setSelectedLocation('matriz');
    setDeliveryInfo(null);
    setCustomerCoords(null);
    setScheduledDate(new Date().toISOString().split('T')[0]);
    setScheduledTime('12:00');
    setOrderDialog(false);
    setCartOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openLightbox = (image, title) => { setLightboxImage(image); setLightboxTitle(title); };
  const closeLightbox = () => { setLightboxImage(null); setLightboxTitle(''); };

  // Cake configurator
  const openCakeConfigurator = (cake) => {
    setSelectedCake(cake);
    setCakeConfig({ size: 'mediana', sabor: '', relleno: '', exterior: '', topping: '' });
    setCakeConfigDialog(true);
  };

  const isCakeConfigComplete = () => cakeConfig.sabor && cakeConfig.relleno && cakeConfig.exterior && cakeConfig.topping;

  const getCakePrice = () => {
    if (!selectedCake) return 0;
    const sizeOption = selectedCake.sizes?.find(s => s.id === cakeConfig.size);
    const basePrice = sizeOption?.price || 0;
    const rellenoExtra = cakeConfigOptions.rellenos.find(r => r.id === cakeConfig.relleno)?.extraPrice || 0;
    const toppingExtra = cakeConfigOptions.toppings.find(t => t.id === cakeConfig.topping)?.extraPrice || 0;
    return basePrice + rellenoExtra + toppingExtra;
  };

  const addCakeToCart = () => {
    if (!isCakeConfigComplete()) { toast.error('Por favor completa todas las opciones de configuración'); return; }
    const sizeOption = selectedCake.sizes.find(s => s.id === cakeConfig.size);
    const saborName = cakeConfigOptions.sabores.find(s => s.id === cakeConfig.sabor)?.name;
    const rellenoOption = cakeConfigOptions.rellenos.find(r => r.id === cakeConfig.relleno);
    const rellenoName = rellenoOption?.name?.replace(' (+$4)', '');
    const rellenoExtra = rellenoOption?.extraPrice || 0;
    const exteriorName = cakeConfigOptions.exteriores.find(e => e.id === cakeConfig.exterior)?.name;
    const toppingOption = cakeConfigOptions.toppings.find(t => t.id === cakeConfig.topping);
    const toppingName = toppingOption?.name?.replace(' (+$4)', '');
    const toppingExtra = toppingOption?.extraPrice || 0;
    const configDescription = `${sizeOption.name} (${sizeOption.portions}) - Sabor: ${saborName}, Relleno: ${rellenoName}, Exterior: ${exteriorName}, Topping: ${toppingName}`;
    setCart([...cart, {
      type: 'Pastel', name: selectedCake.name, size: configDescription,
      price: sizeOption.price + rellenoExtra + toppingExtra, quantity: 1
    }]);
    setCakeConfigDialog(false);
  };

  const addSimpleCakeToCart = (cake) => {
    if (cake.isConfigurable || cake.price === 0) {
      toast.error('Este producto requiere personalización. Por favor, usa las opciones de configuración o contáctanos por WhatsApp.');
      return;
    }
    setCart([...cart, { type: 'Pastel', name: cake.name, size: cake.portions || 'Tamaño estándar', price: cake.price, quantity: 1 }]);
  };

  // Tres Leches
  const openTresLechesConfigurator = (cake) => {
    setSelectedCake(cake);
    setTresLechesConfig({ isCombo: false, medianaFlavor: '', grandeFlavor: '', singleSize: 'mediana', singleFlavor: '' });
    setTresLechesDialog(true);
  };

  const calculateTresLechesPrice = () => {
    if (!selectedCake) return 0;
    if (tresLechesConfig.isCombo) {
      const medianaFlavor = selectedCake.flavors.find(f => f.id === tresLechesConfig.medianaFlavor);
      const grandeFlavor = selectedCake.flavors.find(f => f.id === tresLechesConfig.grandeFlavor);
      const isStandardCombo = medianaFlavor?.comboGroup === 'standard' && grandeFlavor?.comboGroup === 'standard';
      const isPremiumCombo = medianaFlavor?.comboGroup === 'premium' && grandeFlavor?.comboGroup === 'premium'
        && tresLechesConfig.medianaFlavor === tresLechesConfig.grandeFlavor;
      if (isStandardCombo) return 28;
      if (isPremiumCombo) return 28 + (medianaFlavor?.extraPrice || 0) + (grandeFlavor?.extraPrice || 0);
      const pequeña = selectedCake.sizes.find(s => s.id === 'mediana');
      const grande = selectedCake.sizes.find(s => s.id === 'grande');
      return pequeña.price + (medianaFlavor?.extraPrice || 0) + grande.price + (grandeFlavor?.extraPrice || 0);
    }
    const sizeOption = selectedCake.sizes.find(s => s.id === tresLechesConfig.singleSize);
    const flavorOption = selectedCake.flavors.find(f => f.id === tresLechesConfig.singleFlavor);
    return (sizeOption?.price || 0) + (flavorOption?.extraPrice || 0);
  };

  const isValidCombo = () => {
    if (!selectedCake || !tresLechesConfig.medianaFlavor || !tresLechesConfig.grandeFlavor) return false;
    const medianaFlavor = selectedCake.flavors.find(f => f.id === tresLechesConfig.medianaFlavor);
    const grandeFlavor = selectedCake.flavors.find(f => f.id === tresLechesConfig.grandeFlavor);
    if (medianaFlavor?.comboGroup === 'standard' && grandeFlavor?.comboGroup === 'standard') return true;
    if (medianaFlavor?.comboGroup === 'premium' && grandeFlavor?.comboGroup === 'premium'
        && tresLechesConfig.medianaFlavor === tresLechesConfig.grandeFlavor) return true;
    return false;
  };

  const addTresLechesToCart = () => {
    if (tresLechesConfig.isCombo) {
      if (!tresLechesConfig.medianaFlavor || !tresLechesConfig.grandeFlavor) {
        toast.error('Por favor selecciona los sabores para ambos tamaños');
        return;
      }
      const medianaFlavor = selectedCake.flavors.find(f => f.id === tresLechesConfig.medianaFlavor);
      const grandeFlavor = selectedCake.flavors.find(f => f.id === tresLechesConfig.grandeFlavor);
      const price = calculateTresLechesPrice();
      const isPromo = isValidCombo();
      setCart([...cart, {
        type: 'Pastel',
        name: `${selectedCake.name}${isPromo ? ' - COMBO' : ''}`,
        size: `Pequeña (${medianaFlavor.name.replace(' (+$2)', '')}) + Grande (${grandeFlavor.name.replace(' (+$2)', '')})`,
        price, quantity: 1
      }]);
    } else {
      if (!tresLechesConfig.singleFlavor) { toast.error('Por favor selecciona un sabor'); return; }
      const sizeOption = selectedCake.sizes.find(s => s.id === tresLechesConfig.singleSize);
      const flavorOption = selectedCake.flavors.find(f => f.id === tresLechesConfig.singleFlavor);
      setCart([...cart, {
        type: 'Pastel', name: selectedCake.name,
        size: `${sizeOption.name} - ${flavorOption.name.replace(' (+$2)', '')}`,
        price: calculateTresLechesPrice(), quantity: 1
      }]);
    }
    setTresLechesDialog(false);
  };

  const cartSheetProps = {
    cartOpen, setCartOpen, cart, removeFromCart, getTotalCart,
    deliveryMethod, setDeliveryMethod, deliveryInfo,
    selectedLocation, setSelectedLocation,
    customerName, setCustomerName, customerPhone, setCustomerPhone,
    scheduledDate, setScheduledDate, scheduledTime, setScheduledTime,
    comments, setComments, loadingDelivery, deliveryError,
    handleGetLocation, handleWhatsAppOrder, generateOrderNumber, orderNumber,
    validateScheduledTime, getBusinessHoursForDate, isSubmitting
  };

  return (
    <div className="min-h-screen bg-cream">
      <Seo
        title="Brazolovers | Brazos Gitanos Artesanales en Quito"
        description="Pastelería artesanal en Quito con más de 40 sabores únicos de brazos gitanos. Pasteles, galletas y más. Pide a domicilio o retira en nuestros dos locales."
        path="/"
        jsonLd={[getOrganizationJsonLd(), getWebSiteJsonLd(), ...getLocalBusinessJsonLd(), getFaqJsonLd()]}
      />
      {lightboxImage && (
        <Suspense fallback={null}>
          <ImageLightbox image={lightboxImage} title={lightboxTitle} onClose={closeLightbox} />
        </Suspense>
      )}

      <Header ref={headerRef} showAfterHoursWarning={showAfterHoursWarning} openStatus={openStatus} cartSheetProps={cartSheetProps} />

      <HeroSection businessInfo={businessInfo} showAfterHoursWarning={showAfterHoursWarning} />
      <ServicesSection services={services} />

      <ProductsSection
        brazos={brazosGitanos}
        filteredBrazos={filteredBrazos}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        brazosSearchTerm={brazosSearchTerm}
        setBrazosSearchTerm={setBrazosSearchTerm}
        showAllBrazos={showAllBrazos}
        setShowAllBrazos={setShowAllBrazos}
        loadingProducts={loadingProducts}
        openLightbox={openLightbox}
        openProductDetail={openProductDetail}
        getImageUrl={getImageUrl}
        BRAZOS_INITIAL_COUNT={BRAZOS_INITIAL_COUNT}
      />

      <GalletasSection
        galletas={galletas}
        galletasEspeciales={galletasEspeciales}
        packs={packs}
        openLightbox={openLightbox}
        openProductDetail={openProductDetail}
        handleOrderClick={handleOrderClick}
        getImageUrl={getImageUrl}
        loadingProducts={loadingProducts}
      />

      <CakesSection
        pasteles={pasteles}
        openLightbox={openLightbox}
        openCakeConfigurator={openCakeConfigurator}
        openTresLechesConfigurator={openTresLechesConfigurator}
        addSimpleCakeToCart={addSimpleCakeToCart}
        getImageUrl={getImageUrl}
      />

      <PanaderiaSection
        panaderia={panaderia}
        openLightbox={openLightbox}
        openProductDetail={openProductDetail}
        getImageUrl={getImageUrl}
        loadingProducts={loadingProducts}
      />

      <DesayunosSection
        desayunos={desayunos}
        openLightbox={openLightbox}
        openProductDetail={openProductDetail}
        getImageUrl={getImageUrl}
      />

      <MenuSaladoSection
        menuSalado={menuSalado}
        openLightbox={openLightbox}
        openProductDetail={openProductDetail}
        getImageUrl={getImageUrl}
      />

      <BebidasSection
        bebidasFrias={bebidasFrias}
        bebidasCalientes={bebidasCalientes}
        cart={cart}
        setCart={setCart}
        openLightbox={openLightbox}
        openProductDetail={openProductDetail}
        getImageUrl={getImageUrl}
      />

      <AboutSection />
      <ContactSection locations={locations} />
      <ReviewsCarousel
        googleReviews={googleReviews}
        loadingReviews={loadingReviews}
        reviewsPerPage={reviewsPerPage}
        autoScrollInterval={autoScrollInterval}
      />
      <LeaveReviewSection locations={locations} dynamicLocations={dynamicLocations} />
      <FaqSection />
      <Footer businessInfo={businessInfo} />

      {mounted && (
      <Suspense fallback={null}>
      {orderDialog && (
      <OrderDialog
        orderDialog={orderDialog}
        setOrderDialog={setOrderDialog}
        selectedProduct={selectedProduct}
        productType={productType}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        selectedFlavors={selectedFlavors}
        customQuantity={customQuantity}
        setCustomQuantity={setCustomQuantity}
        setSelectedFlavors={setSelectedFlavors}
        galletas={galletas}
        handleFlavorChange={handleFlavorChange}
        getTotalSelectedFlavors={getTotalSelectedFlavors}
        calculateCustomPrice={calculateCustomPrice}
        addToCart={addToCart}
      />
      )}
      {cakeConfigDialog && (
      <CakeConfigurator
        cakeConfigDialog={cakeConfigDialog}
        setCakeConfigDialog={setCakeConfigDialog}
        selectedCake={selectedCake}
        cakeConfig={cakeConfig}
        setCakeConfig={setCakeConfig}
        isCakeConfigComplete={isCakeConfigComplete}
        getCakePrice={getCakePrice}
        addCakeToCart={addCakeToCart}
      />
      )}
      {tresLechesDialog && (
      <TresLechesConfigurator
        tresLechesDialog={tresLechesDialog}
        setTresLechesDialog={setTresLechesDialog}
        selectedCake={selectedCake}
        tresLechesConfig={tresLechesConfig}
        setTresLechesConfig={setTresLechesConfig}
        calculateTresLechesPrice={calculateTresLechesPrice}
        isValidCombo={isValidCombo}
        addTresLechesToCart={addTresLechesToCart}
      />
      )}
      {panJamonDialog && (
      <PanJamonDialog
        panJamonDialog={panJamonDialog}
        setPanJamonDialog={setPanJamonDialog}
        panJamonOption={panJamonOption}
        setPanJamonOption={setPanJamonOption}
        panaderia={panaderia}
        cart={cart}
        setCart={setCart}
      />
      )}
      {productDetailDialog && (
      <ProductModal
        productDetailDialog={productDetailDialog}
        setProductDetailDialog={setProductDetailDialog}
        detailProduct={detailProduct}
        detailProductType={detailProductType}
        detailSelectedSize={detailSelectedSize}
        setDetailSelectedSize={setDetailSelectedSize}
        detailSelectedOption={detailSelectedOption}
        setDetailSelectedOption={setDetailSelectedOption}
        detailQuantity={detailQuantity}
        setDetailQuantity={setDetailQuantity}
        addToCartFromDetail={addToCartFromDetail}
      />
      )}
      </Suspense>
      )}
    </div>
  );
};

export default Home;
